/* eslint-disable react-hooks/exhaustive-deps */
import { Avatar, Box, Button, Paper, Snackbar, Stack, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import { Icon } from '@iconify/react';
import { observer } from 'mobx-react-lite';
import { API_AUTH_URL, userApi } from 'api';
import { ChangeEvent, useMemo, useState } from 'react';
import { personalDataStore } from 'stores/personalDataStore';
import { panelSx } from 'components/Profile/profileStyles';

const AVATAR_MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

const UploadAvatar = observer(() => {
  const { user } = personalDataStore;
  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.avatar || '');
  const [error, setError] = useState<string | null>(null);
  const [avatarUploadingOpen, setAvatarUploadingOpen] = useState(false);

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  const handleAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!user?.id) return;

    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Загрузите файл изображения');
      return;
    }

    if (file.size > AVATAR_MAX_SIZE_BYTES) {
      setError('Размер файла превышает 10 МБ');
      return;
    }

    setError(null);
    setAvatarUploadingOpen(true);

    try {
      const url = await userApi.uploadFile(file, user?.id as string);
      setAvatarUrl(url);
      setAvatarUploadingOpen(false);
    } catch {
      setError('Произошла ошибка при загрузке файла');
      setAvatarUploadingOpen(false);
    }
  };

  const isForce = useMemo(
    () => location.search.includes('force=true') || !personalDataStore.user?.lastName,
    [location.search]
  );

  const handleCloseAvatarUploading = (_?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setAvatarUploadingOpen(false);
  };

  if(isForce) return null

  const fullPathAvatar = avatarUrl?.includes('http') ? avatarUrl : `${API_AUTH_URL.slice(0, -1)}${avatarUrl}`

  return (
    <Paper elevation={0} sx={panelSx}>
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack
          direction="row"
          spacing={3}
          alignItems={{ sm: 'center' }}
        >
          <Avatar
            src={fullPathAvatar}
            alt="Аватар пользователя"
            sx={{
              width: 70,
              height: 70,
              border: (t) => `2px solid ${t.palette.background.paper}`,
              boxShadow: (t) => `0 0 0 1px ${t.palette.grey[300]}`
            }}
            imgProps={{ referrerPolicy: 'no-referrer', crossOrigin: 'anonymous' }}
          />
          {/* <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 18, lineHeight: '28px', fontWeight: 600, color: 'text.primary' }}
              noWrap
            >
              {fullName || 'Ваш профиль'}
            </Typography>
            {user?.email ? (
              <Typography variant="body2" color="text.secondary" noWrap>
                {user.email}
              </Typography>
            ) : null}
          </Box> */}
          <Box sx={{ flexShrink: 0 }}>
            <Button
              component="label"
              variant="outlined"
              color="inherit"
              aria-label="Загрузить аватар"
              tabIndex={0}
              startIcon={<Icon icon="mdi:camera-outline" />}
            >
              Загрузить аватар
              <input hidden accept="image/*" type="file" onChange={handleAvatarChange} />
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              JPG/PNG, до 10 МБ
            </Typography>
          </Box>
        </Stack>
        {error ? (
          <Alert
            severity="error"
            variant="outlined"
            role="alert"
            sx={{ mt: 3, borderRadius: (t) => `${t.shape.borderRadius}px` }}
          >
            {error}
          </Alert>
        ) : null}
      </Box>
      <Snackbar
        open={avatarUploadingOpen}
        autoHideDuration={3000}
        onClose={handleCloseAvatarUploading}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        aria-label="Загрузка аватара"
      >
        <Alert
          onClose={handleCloseAvatarUploading}
          severity="info"
          sx={{ width: '100%' }}
          variant="filled"
        >
          Аватар загружается
        </Alert>
      </Snackbar>
    </Paper>
  );
});

export default UploadAvatar;
